import React from 'react';
import { Row, Col, Button, Select, Skeleton, Layout, DatePicker, Space } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useAtom } from 'jotai';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { showUserNameBreakdownFeatureSelector } from '@store/common/selectors';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import useExport from '@components/export/useExport';
import { GRANULARITY } from '@utils/granularity';
import { apiAccountsDetailsAtom, intervalAtom, granularityAtom } from '../atoms';
import { useGetCustomerList } from '../hooks/dataFetcherhooks';
import AccountList from '../AccountView/AccountList/AccountList';
import GranularityPicker from '../UsageDashboardHeader/UsageSummary/DatePicker/GranularityPicker';
import { utcMoment } from '../utils';
import { SiderLayout, SiderWrapper, SiderHeader, SiderContent } from '../styled';
import { StyledH4 } from '../AccountView/styled';
import {
  breakdownChartOrderAtom,
  breakdownTableOrderAtom,
  usageTypeFilterAtom,
  UsageTypeFilter,
} from './atoms';
import {
  getBreakdownDimensions,
  USAGE_TYPE_ALL,
  USAGE_TYPE_MCP_ONLY,
  USAGE_TYPE_API_ONLY,
} from './constants';
import { useBreakdownChartData } from './ApiUsageBreakdownChart';
import SortableChartItem from './SortableChartItem';
import SortableTableItem from './SortableTableItem';
import ApiUsageBreakdownFilters from './ApiUsageBreakdownFilters';

import { ApiUsageScopeToggle } from '../apiUsageScope';

const buildBreakdownExportConfig = (showUserName: boolean) => ({
  page: { key: 'usageDashboard', title: 'API Usage Breakdown' },
  sections: [
    { key: 'ud-api-breakdown-timeseries' },
    ...getBreakdownDimensions(showUserName).map((dim) => ({
      key: `ud-api-breakdown-${dim.key}`,
    })),
  ],
});

interface ApiUsageBreakdownDrawerProps {
  hasContext: boolean;
}

/**
 * Outer wrapper: handles customer list fetching without subscribing to account atoms.
 * Mirrors the pattern in UsageDashboard/index.tsx.
 */
const ApiUsageBreakdownDrawer = ({ hasContext }: ApiUsageBreakdownDrawerProps) => {
  const customerList = useGetCustomerList({ enabled: !hasContext });

  return (
    <ApiUsageBreakdownContent isCustomerListFetching={!hasContext && customerList.isFetching} />
  );
};

/**
 * Inner content: subscribes to atoms and renders the UI.
 * Layout mirrors the main UsageDashboard: sidebar (accounts) | header + content.
 */
const ApiUsageBreakdownContent = ({
  isCustomerListFetching,
}: {
  isCustomerListFetching: boolean;
}) => {
  // Filter state - scoped to this drawer's Jotai Provider
  const [interval, setInterval] = useAtom(intervalAtom);
  const [granularity, setGranularity] = useAtom(granularityAtom);
  const [apiAccounts, setApiAccounts] = useAtom(apiAccountsDetailsAtom);

  // Breakdown-specific state
  const [usageType, setUsageType] = useAtom(usageTypeFilterAtom);
  const [chartOrder, setChartOrder] = useAtom(breakdownChartOrderAtom);
  const [tableOrder, setTableOrder] = useAtom(breakdownTableOrderAtom);
  const showUserName = useSelector(showUserNameBreakdownFeatureSelector);
  const breakdownExportConfig = React.useMemo(
    () => buildBreakdownExportConfig(showUserName),
    [showUserName],
  );
  const { triggerDownload } = useExport({ config: breakdownExportConfig });
  const sensors = useSensors(useSensor(PointerSensor));
  const { seriesData, isFetching, isError } = useBreakdownChartData();

  const handleChartDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const newOrder = Array.from(chartOrder);
    const [item] = newOrder.splice(chartOrder.indexOf(active.id as string), 1);
    newOrder.splice(chartOrder.indexOf(over.id as string), 0, item);
    setChartOrder(newOrder);
  };

  const handleTableDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const newOrder = Array.from(tableOrder);
    const [item] = newOrder.splice(tableOrder.indexOf(active.id as string), 1);
    newOrder.splice(tableOrder.indexOf(over.id as string), 0, item);
    setTableOrder(newOrder);
  };

  // Reuses same date handling logic as UsageSummary
  const onRangeChange = (dates) => {
    if (dates) {
      const today: any = moment();
      const from: any = dates[0].startOf('month');
      let to: any = dates[1].endOf('month');
      if (dates[1].isSame(today, 'month')) {
        to = today;
      }
      setInterval([from, to]);
      if (granularity === GRANULARITY.P1W) {
        setGranularity(GRANULARITY.P1D);
      }
    }
  };

  const disabledDate = (current) => {
    const calendarDateMoment = utcMoment(current);
    return calendarDateMoment && calendarDateMoment > utcMoment().endOf('month');
  };

  return (
    <SiderLayout>
      {/* Left sidebar: API Accounts */}
      <SiderWrapper>
        {isCustomerListFetching ? (
          <Skeleton paragraph={{ rows: 20 }} active />
        ) : (
          <div
            style={{ textAlign: 'left', minHeight: '100%', maxHeight: '100%', overflowY: 'auto' }}
          >
            <StyledH4>Select Accounts To View</StyledH4>
            <hr style={{ margin: 0, padding: 0 }} />
            {apiAccounts.length > 0 ? (
              <AccountList
                accountDetails={apiAccounts}
                setAccountDetails={setApiAccounts}
                label="API Accounts"
              />
            ) : (
              <span style={{ color: '#999', padding: 16 }}>No API accounts available</span>
            )}
          </div>
        )}
      </SiderWrapper>

      {/* Main content area */}
      <Layout>
        {/* Header: title bar + filters */}
        <SiderHeader>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 16,
              padding: '10px 16px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <span style={{ fontSize: 18, fontWeight: 600 }}>API Usage Details</span>
              <span style={{ fontSize: 14, fontWeight: 400, color: '#595959', marginLeft: 16 }}>
                Filter:
              </span>
              <Select
                size="small"
                value={usageType}
                onChange={(val: UsageTypeFilter) => setUsageType(val)}
                style={{ width: 120 }}
                options={[
                  { value: USAGE_TYPE_ALL, label: 'All' },
                  { value: USAGE_TYPE_MCP_ONLY, label: 'MCP-Only' },
                  { value: USAGE_TYPE_API_ONLY, label: 'API-Only' },
                ]}
              />
            </div>
            <Space size={16} wrap>
              <ApiUsageScopeToggle />
              <GranularityPicker />
              <DatePicker.RangePicker
                onChange={onRangeChange}
                picker="month"
                disabledDate={disabledDate}
                defaultValue={interval}
                style={{ backgroundColor: '#e9edf0' }}
              />
              <Button size="small" icon={<DownloadOutlined />} onClick={triggerDownload}>
                Export
              </Button>
            </Space>
          </div>
        </SiderHeader>

        {/* Content: filters, charts, tables */}
        <SiderContent style={{ backgroundColor: 'white', padding: 16 }}>
          <ApiUsageBreakdownFilters />

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleChartDragEnd}
          >
            <SortableContext items={chartOrder} strategy={verticalListSortingStrategy}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
                {chartOrder.map((metric) => (
                  <SortableChartItem
                    key={metric}
                    metric={metric}
                    seriesData={seriesData}
                    isFetching={isFetching}
                    isError={isError}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleTableDragEnd}
          >
            <SortableContext items={tableOrder} strategy={rectSortingStrategy}>
              <Row gutter={[12, 12]}>
                {tableOrder.map((dimKey) => (
                  <Col key={dimKey} xs={24} md={12}>
                    <SortableTableItem dimension={dimKey} />
                  </Col>
                ))}
              </Row>
            </SortableContext>
          </DndContext>
        </SiderContent>
      </Layout>
    </SiderLayout>
  );
};

export default ApiUsageBreakdownDrawer;
